<template>
  <v-container class="my-5">
    <v-layout row wrap class="justify-center">
      <v-flex xs12>
        <v-card-title class="mb-4">
          <v-btn icon @click="$router.go(-1)" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="headline font-weight-bold">Asignación de Alumnos</span>
          <v-spacer></v-spacer>
        </v-card-title>

        <!-- Información General -->
        <v-card class="mb-4">
          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <div class="text-center">
                  <div class="display-1 font-weight-bold primary--text">{{ students.length }}</div>
                  <div class="caption grey--text">Alumnos Totales</div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="text-center">
                  <div class="display-1 font-weight-bold green--text">{{ enrolledStudents }}</div>
                  <div class="caption grey--text">Con Inscripciones</div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="text-center">
                  <div class="display-1 font-weight-bold orange--text">{{ totalEnrollments }}</div>
                  <div class="caption grey--text">Total Inscripciones</div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="text-center">
                  <div class="display-1 font-weight-bold info--text">{{ avgEnrollmentsPerStudent }}</div>
                  <div class="caption grey--text">Promedio por Alumno</div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Tabs para organizar vista -->
        <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-4">
          <v-tab>
            <v-icon left>mdi-account-multiple</v-icon>
            Listado de Alumnos
          </v-tab>
          <v-tab>
            <v-icon left>mdi-view-grid</v-icon>
            Matriz Asignación
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="transparent">
          <!-- Tab 1: Listado de Alumnos -->
          <v-tab-item>
            <v-card class="elevation-1">
              <v-card-subtitle class="pa-4">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <v-icon small class="mr-2">mdi-information</v-icon>
                    Listado de alumnos registrados en la academia
                  </div>
                  <v-text-field
                    v-model="studentSearch"
                    prepend-inner-icon="mdi-magnify"
                    label="Buscar alumno..."
                    outlined
                    dense
                    style="max-width: 300px;"
                  ></v-text-field>
                </div>
              </v-card-subtitle>
              <v-data-table
                :headers="studentHeaders"
                :items="filteredStudents"
                class="elevation-0"
                :no-data-text="'Sin alumnos registrados'">

                <template v-slot:item.name="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" color="primary" class="white--text mr-2">
                      <span class="caption">{{ getInitials(item.name) }}</span>
                    </v-avatar>
                    <div>
                      <div class="font-weight-bold">{{ item.name }}</div>
                      <div class="caption grey--text">ID: {{ item.id }}</div>
                    </div>
                  </div>
                </template>

                <template v-slot:item.level="{ item }">
                  <v-chip small :color="getLevelColor(item.level)">
                    {{ getLevelLabel(item.level) }}
                  </v-chip>
                </template>

                <template v-slot:item.clinics="{ item }">
                  <div class="text-center">
                    <v-chip small>{{ getStudentClinicCount(item.id) }}</v-chip>
                  </div>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small class="mr-2" v-bind="attrs" v-on="on" @click="viewStudentDetails(item)">
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>Ver detalles</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-bind="attrs" v-on="on" @click="enrollStudent(item)">
                        <v-icon small color="green">mdi-plus-circle</v-icon>
                      </v-btn>
                    </template>
                    <span>Inscribir a clínica</span>
                  </v-tooltip>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>

          <!-- Tab 2: Matriz de Asignación -->
          <v-tab-item>
            <v-card class="elevation-1">
              <v-card-subtitle class="pa-4">
                <v-icon small class="mr-2">mdi-information</v-icon>
                Matriz de alumnos inscritos por clínica
              </v-card-subtitle>
              <v-card-text>
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Clínica</th>
                        <th class="text-center">Profesor</th>
                        <th class="text-center">Fecha</th>
                        <th class="text-center">Inscritos</th>
                        <th class="text-center">Capacidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="clinic in clinics" :key="clinic.id">
                        <td>
                          <div class="font-weight-bold">{{ getClinicTypeLabel(clinic.type) }}</div>
                          <div class="caption grey--text">{{ clinic.description }}</div>
                        </td>
                        <td class="text-center">{{ getTeacherName(clinic.teacher_id) }}</td>
                        <td class="text-center">{{ formatDate(clinic.date) }}</td>
                        <td class="text-center">
                          <v-chip small color="info" text-color="white">
                            {{ clinic.enrolled }}
                          </v-chip>
                        </td>
                        <td class="text-center">
                          <v-chip small>{{ clinic.capacity }}</v-chip>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-flex>
    </v-layout>

    <!-- Diálogo de detalles del alumno -->
    <StudentDetailDialog
      :dialog.sync="detailDialog"
      :student="selectedStudent"
      :student-clinics="selectedStudentClinics"
    />

    <!-- Diálogo para inscribir a clínica -->
    <EnrollmentDialog
      :dialog.sync="enrollDialog"
      :student="selectedStudent"
      :available-clinics="availableClinics"
      @enroll="confirmEnrollment"
    />

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import StudentDetailDialog from './components/StudentDetailDialog.vue'
import EnrollmentDialog from './components/EnrollmentDialog.vue'

export default {
  name: 'AcademyStudents',
  components: {
    StudentDetailDialog,
    EnrollmentDialog
  },
  data () {
    return {
      activeTab: 0,
      studentSearch: '',
      detailDialog: false,
      enrollDialog: false,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      selectedStudent: null,
      selectedStudentClinics: [],
      studentHeaders: [
        { text: 'Alumno', value: 'name', width: '30%' },
        { text: 'Nivel', value: 'level', width: '20%' },
        { text: 'Clínicas Inscritas', value: 'clinics', width: '15%', align: 'center' },
        { text: 'Acciones', value: 'actions', width: '20%', sortable: false, align: 'center' }
      ],
      // MOCK DATA
      students: [
        { id: 1, name: 'Juan García López', level: 'intermedio', phone: '+34 666 111 222', email: 'juan.garcia@email.com' },
        { id: 2, name: 'Ana Martínez Rodríguez', level: 'avanzado', phone: '+34 666 333 444', email: 'ana.martinez@email.com' },
        { id: 3, name: 'Roberto Díaz Sánchez', level: 'basico', phone: '+34 666 555 666', email: 'roberto.diaz@email.com' },
        { id: 4, name: 'Carmen López Pérez', level: 'intermedio', phone: '+34 666 777 888', email: 'carmen.lopez@email.com' },
        { id: 5, name: 'Miguel Fernández González', level: 'basico', phone: '+34 666 999 000', email: 'miguel.fernandez@email.com' },
        { id: 6, name: 'Sofía Ruiz Martínez', level: 'avanzado', phone: '+34 666 121 314', email: 'sofia.ruiz@email.com' }
      ],
      clinics: [
        {
          id: 1,
          teacher_id: 1,
          type: 'swing',
          date: '2025-08-08',
          time: '09:00',
          capacity: 6,
          enrolled: 4,
          description: 'Técnica de Swing Avanzado'
        },
        {
          id: 2,
          teacher_id: 2,
          type: 'putting',
          date: '2025-08-09',
          time: '10:00',
          capacity: 8,
          enrolled: 7,
          description: 'Masterclass de Putting'
        },
        {
          id: 3,
          teacher_id: 3,
          type: 'pitch',
          date: '2025-08-10',
          time: '14:00',
          capacity: 6,
          enrolled: 2,
          description: 'Técnica de Pitch y Chip'
        }
      ],
      teachers: [
        { id: 1, name: 'Carlos López García' },
        { id: 2, name: 'María Rodríguez' },
        { id: 3, name: 'Fernando Ruiz' }
      ],
      // Asignaciones: student_id -> [clinic_ids]
      enrollments: {
        1: [1, 2],
        2: [2, 3],
        3: [1],
        4: [2],
        5: [],
        6: [3]
      }
    }
  },
  computed: {
    filteredStudents () {
      if (!this.studentSearch) return this.students
      return this.students.filter(s =>
        s.name.toLowerCase().includes(this.studentSearch.toLowerCase())
      )
    },
    enrolledStudents () {
      return Object.values(this.enrollments).filter(arr => arr.length > 0).length
    },
    totalEnrollments () {
      return Object.values(this.enrollments).reduce((sum, arr) => sum + arr.length, 0)
    },
    avgEnrollmentsPerStudent () {
      return this.students.length > 0 ? (this.totalEnrollments / this.students.length).toFixed(1) : 0
    },
    availableClinics () {
      if (!this.selectedStudent) return []
      const enrolled = this.enrollments[this.selectedStudent.id] || []
      return this.clinics.filter(c => !enrolled.includes(c.id))
    }
  },
  methods: {
    getInitials (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    },
    formatDate (dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleDateString('es-MX', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      })
    },
    getLevelColor (level) {
      const colors = {
        basico: 'green',
        intermedio: 'orange',
        avanzado: 'red'
      }
      return colors[level] || 'grey'
    },
    getLevelLabel (level) {
      const labels = {
        basico: 'Básico',
        intermedio: 'Intermedio',
        avanzado: 'Avanzado'
      }
      return labels[level] || 'Otro'
    },
    getClinicTypeLabel (type) {
      const labels = {
        swing: 'Swing',
        putting: 'Putting',
        pitch: 'Pitch',
        full_swing: 'Swing Completo',
        short_game: 'Juego Corto'
      }
      return labels[type] || 'Otro'
    },
    getTeacherName (teacherId) {
      const teacher = this.teachers.find(t => t.id === teacherId)
      return teacher ? teacher.name : 'Desconocido'
    },
    getStudentClinicCount (studentId) {
      return (this.enrollments[studentId] || []).length
    },
    viewStudentDetails (student) {
      this.selectedStudent = { ...student }
      this.selectedStudentClinics = this.clinics.filter(c =>
        (this.enrollments[student.id] || []).includes(c.id)
      )
      this.detailDialog = true
    },
    enrollStudent (student) {
      this.selectedStudent = { ...student }
      this.enrollDialog = true
    },
    confirmEnrollment (clinicId) {
      if (!this.enrollments[this.selectedStudent.id]) {
        this.$set(this.enrollments, this.selectedStudent.id, [])
      }
      this.enrollments[this.selectedStudent.id].push(clinicId)

      // Actualizar clínica
      const clinic = this.clinics.find(c => c.id === clinicId)
      if (clinic) {
        clinic.enrolled += 1
      }

      this.snackbarMessage = `${this.selectedStudent.name} inscrito correctamente`
      this.snackbarColor = 'success'
      this.snackbar = true
      this.enrollDialog = false
    }
  }
}
</script>

<style scoped>
</style>
